import { useEffect, useState, useCallback } from 'react'
import { apiUrl } from '../lib/api'

export interface CollaborationMetrics {
  from_agent: string
  to_agent: string
  collaboration_count: number
}

export interface ActionOutcome {
  agent: string
  action_count: number
  completed_count: number
  failed_count: number
  pending_count: number
  success_rate: number | null
}

interface PatternsResponse {
  action_outcomes: ActionOutcome[]
  collaboration_metrics: CollaborationMetrics[]
  generated_at: string
}

export function useCoordinationMetrics() {
  const [metrics, setMetrics] = useState<{
    actionOutcomes: ActionOutcome[]
    collaborationMetrics: CollaborationMetrics[]
  }>({ actionOutcomes: [], collaborationMetrics: [] })
  const [score, setScore] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const calculateScore = useCallback((
    outcomes: ActionOutcome[],
    collaborations: CollaborationMetrics[]
  ): number => {
    if (outcomes.length === 0) return 0

    // Calculate success rate component (0-40 points)
    const avgSuccessRate = outcomes.reduce((sum, o) => 
      sum + (o.success_rate || 0), 0
    ) / outcomes.length
    const successScore = (avgSuccessRate / 100) * 40

    // Calculate activity component (0-30 points)
    const totalActions = outcomes.reduce((sum, o) => sum + o.action_count, 0)
    const activityScore = Math.min((totalActions / 100) * 30, 30)

    // Calculate collaboration component (0-30 points)
    const totalCollaborations = collaborations.reduce((sum, c) => 
      sum + c.collaboration_count, 0
    )
    const collaborationScore = Math.min((totalCollaborations / 50) * 30, 30)

    return Math.round(successScore + activityScore + collaborationScore)
  }, [])

  const fetchMetrics = useCallback(async () => {
    try {
      const response = await fetch(apiUrl('/api/patterns'))
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      const data: PatternsResponse = await response.json()
      
      setMetrics({
        actionOutcomes: data.action_outcomes,
        collaborationMetrics: data.collaboration_metrics
      })
      
      const newScore = calculateScore(data.action_outcomes, data.collaboration_metrics)
      setScore(newScore)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch metrics')
    } finally {
      setLoading(false)
    }
  }, [calculateScore])

  useEffect(() => {
    fetchMetrics()
    
    // Poll every 30 seconds
    const interval = setInterval(fetchMetrics, 30000)
    return () => clearInterval(interval)
  }, [fetchMetrics])

  return { score, metrics, loading, error }
}
